import React, { useEffect, useMemo } from 'react';
import { Button } from '@shared/ui/button';
import { OrderSummary, SafeAndSecure } from './SharedCards';
import { useConsumerBill } from '../../../../shared/hook';
import { useCombinedBillsServices } from '../../../hook';
import { useSearchParams, useLocation } from 'react-router-dom';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';

export const SelectBillsStep = ({ data, onDataChange, onNext, allStepData }: any) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const type = searchParams.get('type') || 'bills'; // 'bills', 'services', or 'installment'
  const consumerNoFromUrl = searchParams.get('consumer_no');
  const selectedIdFromUrl = searchParams.get('selected_id');
  
  const installmentDataRaw = sessionStorage.getItem('installmentData');
  const installmentData = type === 'installment' && installmentDataRaw ? JSON.parse(installmentDataRaw) : null;

  const { selectedBills } = data || {};
  const { remoteUtilityId, remoteConsumerNumber } = getLoginDataFromStorage();
  const consumerDetails = localStorage.getItem("loginResult");
  const parsed = consumerDetails ? JSON.parse(consumerDetails) : null;
  const consumerName = parsed?.result ? `${parsed.result.firstName} ${parsed.result.lastName}`.trim() : 'NA';
  const consumerId = parsed?.result?.consumerId || '';

  const { data: billsDataResponse } = useCombinedBillsServices({
    remote_utility_id: remoteUtilityId,
    consumer_number: consumerNoFromUrl || remoteConsumerNumber,
    is_pagination_required: true,
    type_filter: type === 'installment' ? undefined : type
  });

  const availableBillsList = useMemo(() => {
    if (type === 'installment' && installmentData) {
      return [{
        id: `Installment-${installmentData.installmentNo}`,
        month: `Installment ${installmentData.installmentNo}`,
        dueDate: installmentData.dueDate || 'NA',
        usage: 'Payment Agreement Installment',
        amount: Number(installmentData.installmentAmount) || 0,
        status: installmentData.statusDisplay || 'NA',
        tags: (installmentData.statusDisplay || '').toLowerCase() === 'unpaid' ? ['Due'] : [],
      }];
    }

    if (billsDataResponse?.result && billsDataResponse?.result.length > 0) {
      return billsDataResponse.result
        .filter((item: any) => (item.Status || '').toLowerCase() !== 'paid')
        .map((item: any) => ({
          id: item.ID || 'N/A',
          month: item.Date || 'NA',
          dueDate: item.Date || 'NA',
          usage: item.Description || item.Category || 'NA',
          amount: Number(item.Amount) || 0,
          status: item.Status || 'NA',
          tags: (item.Status || '').toLowerCase() === 'unpaid' ? ['Due'] : [],
        }));
    }
    return [];
  }, [billsDataResponse, type, installmentData]);

  // Initialize bills selection
  useEffect(() => {
    if (!selectedBills && availableBillsList.length > 0) {
      let initialIds = availableBillsList.map((b: any) => b.id);
      
      if (selectedIdFromUrl) {
        const matchingId = availableBillsList.find((b: any) => String(b.id) === selectedIdFromUrl);
        if (matchingId) {
          initialIds = [matchingId.id];
        }
      }

      onDataChange({ 
        selectedBills: initialIds,
        selectedBillsData: availableBillsList.filter((b: any) => initialIds.includes(b.id))
      });
    }
  }, [selectedBills, onDataChange, availableBillsList, selectedIdFromUrl]);

  const handleToggle = (id: string) => {
    const current = selectedBills || [];
    let newSelected = [...current];
    if (newSelected.includes(id)) {
      newSelected = newSelected.filter(b => b !== id);
    } else {
      newSelected.push(id);
    }
    onDataChange({ 
      selectedBills: newSelected,
      selectedBillsData: availableBillsList.filter((b: any) => newSelected.includes(b.id))
    });
  };

  const handleSelectAll = () => {
    const current = selectedBills || [];
    if (current.length === availableBillsList.length) {
      onDataChange({ selectedBills: [], selectedBillsData: [] });
    } else {
      const allIds = availableBillsList.map((b: any) => b.id);
      onDataChange({ 
        selectedBills: allIds,
        selectedBillsData: availableBillsList
      });
    }
  };

  const currentSelected = selectedBills || [];
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Left Pane */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-xl border p-6 h-full flex flex-col">
          <div className="flex items-center gap-2 mb-6 text-gray-900 font-semibold text-lg">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
            </svg>
            {consumerName} — Select {type === 'services' ? 'Services' : type === 'installment' ? 'Installment' : 'Bills'} to Pay
          </div>

          <div className="space-y-4 flex-1">
            {availableBillsList.map((bill: any) => {
              const isSelected = currentSelected.includes(bill.id);
              return (
                <div 
                  key={bill.id}
                  onClick={() => handleToggle(bill.id)}
                  className={`border-2 rounded-lg p-4 flex items-center justify-between cursor-pointer transition-colors ${isSelected ? 'border-blue-500 bg-blue-50/30' : 'border-gray-200 hover:border-blue-300'}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 border ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}>
                      {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-white"></div>}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-gray-900 text-sm">{bill.month}</span>
                        {bill.tags.map((tag: any, i: any) => (
                          <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${tag.includes('Late') ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-600'}`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="text-xs text-gray-500">
                        {type === 'services' ? 'Service ID' : type === 'installment' ? 'Installment No' : 'Bill ID'}: {bill.id} • Due: {bill.dueDate}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900 mb-1">${bill.amount.toFixed(2)}</div>
                    <span className="bg-red-100 text-red-600 text-[10px] px-2 py-0.5 rounded-full font-medium inline-block">{bill.status}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex items-center justify-between">
            <span onClick={handleSelectAll} className="text-sm text-blue-600 font-medium cursor-pointer">
              {currentSelected.length === availableBillsList.length ? 'Deselect All' : `Select All (${availableBillsList.length})`}
            </span>
            <Button onClick={onNext} disabled={currentSelected.length === 0} className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-6 disabled:opacity-50">
              Continue <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
            </Button>
          </div>
        </div>
      </div>

      {/* Right Pane */}
      <div className="lg:col-span-1">
        <OrderSummary allStepData={allStepData || { 0: { selectedBills: currentSelected, selectedBillsData: availableBillsList.filter((b: any) => currentSelected.includes(b.id)) } }} />
        <SafeAndSecure />
      </div>
    </div>
  );
};
